import { useState, useCallback } from "react";
import { qrListJoinedRoomsFn } from "../firebase";
import { Room } from "../types";

const ROOMS_STORAGE_KEY = "BlinkBli:rooms";

export function useRooms(user: any, isAuthenticated: boolean) {
  const [rooms, setRooms] = useState<Room[]>(() => {
    // Initialize state from localStorage on mount
    try {
      const cached = localStorage.getItem(ROOMS_STORAGE_KEY);
      console.log("🏠 useRooms - Loading from localStorage:", cached);
      if (cached) {
        const parsed = JSON.parse(cached);
        console.log("🏠 useRooms - Parsed rooms:", parsed);
        return parsed;
      }
    } catch (error) {
      console.warn("Failed to load cached rooms:", error);
    }
    console.log("🏠 useRooms - No cached rooms found, starting with empty array");
    return [];
  });
  const [loadingRooms, setLoadingRooms] = useState(false);
  
  console.log("🏠 useRooms - Current rooms state:", rooms);

  const fetchRooms = useCallback(async () => {
    if (!user || !isAuthenticated) {
      console.log("🔐 useRooms - Skipping fetch, user:", !!user, "isAuthenticated:", isAuthenticated);
      return;
    }
    console.log("🔐 useRooms - Fetching rooms for user:", user.uid);
    setLoadingRooms(true);
    try {
      const res: any = await qrListJoinedRoomsFn({ tenantId: "blinkbli" }, user);
      const arr = Array.isArray(res?.rooms) ? res.rooms : [];
      const roomsList = arr.map((r: any) => ({ 
        code: r.code,
        name: r.name,
        lastJoinedAt: typeof r.lastJoinedAt === 'string' ? r.lastJoinedAt : new Date(r.lastJoinedAt).toISOString()
      }));
      
      setRooms(roomsList);
      
      // Save to localStorage
      localStorage.setItem(ROOMS_STORAGE_KEY, JSON.stringify(roomsList));
    } catch (error) {
      console.error("🔐 useRooms - Error fetching rooms:", error);
      setRooms([]);
    } finally {
      setLoadingRooms(false);
    }
  }, [user, isAuthenticated]);

  const addRoom = useCallback((code: string) => {
    const newRoom: Room = {
      code,
      lastJoinedAt: new Date().toISOString()
    };
    
    // Check if room already exists
    const existingIndex = rooms.findIndex(r => r.code === code);
    let updatedRooms: Room[];
    
    if (existingIndex !== -1) {
      // Update existing room
      updatedRooms = [...rooms];
      updatedRooms[existingIndex] = newRoom;
    } else {
      // Add new room
      updatedRooms = [newRoom, ...rooms];
    }
    
    setRooms(updatedRooms);
    localStorage.setItem(ROOMS_STORAGE_KEY, JSON.stringify(updatedRooms));
    console.log("🏠 useRooms - Added/updated room:", code, "Total rooms:", updatedRooms.length);
  }, [rooms]);

  return {
    rooms,
    loadingRooms,
    fetchRooms,
    addRoom
  };
}
